
import { Component, Vue } from 'vue-property-decorator'
import { UserModule } from '@/store/modules/user'
import {
    parseUrlParams,
    removeUrlParams,
    getOIDCState,
    getOIDCNonce,
    clearAllOIDCStorage
} from '@/common/oidc'

/**
 * OIDC 回调页面
 * 处理 IDP 授权后的回调逻辑
 * 
 * 安全注意事项：
 * 1. 立即清除 URL 中的 code 参数，避免留在浏览器历史记录
 * 2. 验证 state 参数，防止 CSRF 攻击
 * 3. 不解析任何 Token，所有验证由后端完成
 */
@Component({
    name: 'OIDCCallback'
})
export default class extends Vue {
    private loading = true
    private error = false
    private errorMessage = ''

    async mounted() {
        await this.handleCallback()
    }

    /**
     * 处理 OIDC 回调
     */
    private async handleCallback() {
        try {
            // 1. 解析 URL 参数
            const params = parseUrlParams()
            
            // 2. 立即清除 URL 中的敏感参数（code、state）
            //    使用 replaceState 避免留在浏览器历史记录中
            removeUrlParams(['code', 'state'])
            
            // 3. 检查是否有错误参数（IDP 返回的错误）
            if (params.error) {
                this.handleError(this.getErrorMessage(params.error, params.error_description))
                return
            }
            
            // 4. 获取授权码和状态参数
            const code = params.code
            const state = params.state
            
            if (!code) {
                this.handleError('未收到授权码，请重新登录')
                return
            }
            
            // 5. 验证 state 参数（防止 CSRF 攻击）
            const savedState = getOIDCState()
            if (!savedState || savedState !== state) {
                this.handleError('安全验证失败（state 不匹配），请重新登录')
                clearAllOIDCStorage()
                return
            }
            
            // 6. 获取保存的 nonce
            const nonce = getOIDCNonce() || ''
            
            // 7. 将 code 传递给后端交换 Token
            //    前端不解析任何 Token，所有验证由后端完成
            const result = await UserModule.oidcLogin({ code, state, nonce })
            
            // 8. 清除临时存储的 state 和 nonce
            clearAllOIDCStorage()
            
            if (result.status === 200) {
                // 9. 登录成功，跳转到首页或之前的页面
                // 用硬跳转避免与路由守卫的 replace 导航发生冲突（Redirected when going from ...）
                const redirect = (this.$route.query.redirect as string) || '/'
                window.location.href = redirect
            } else {
                this.handleError(result.message || '登录失败，请重试')
            }
        } catch (err: any) {
            clearAllOIDCStorage()
            this.handleError(err.message || '登录过程中发生错误，请重试')
        }
    }

    /**
     * 处理错误
     */
    private handleError(message: string) {
        this.loading = false
        this.error = true
        this.errorMessage = message
    }

    /**
     * 获取友好的错误消息
     */
    private getErrorMessage(error: string, description?: string): string {
        const errorMessages: Record<string, string> = {
            'access_denied': '您拒绝了授权请求',
            'invalid_request': '请求参数无效',
            'unauthorized_client': '客户端未授权',
            'unsupported_response_type': '不支持的响应类型',
            'invalid_scope': '请求的权限范围无效',
            'server_error': '服务器内部错误',
            'temporarily_unavailable': '服务暂时不可用'
        }
        
        return errorMessages[error] || description || `授权失败: ${error}`
    }

    /**
     * 返回登录页
     */
    private goToLogin() {
        this.$router.replace('/login')
    }
}
